import useCreateSale from '@/hooks/queries/sale/useCreateSale';
import useGetServiceByIdOrCode from '@/hooks/queries/services/useGetServiceByIdOrCode';
import useGetUser from '@/hooks/queries/user/useGetUser';
import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import {
  birthdateSchema,
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  phoneSchema
} from '@/schemas/common/schemas';
import api from '@/services/api/api';
import { TOrganizationFAQ } from '@/services/api/organization-api';
import { Service } from '@/services/api/services-api';
import { TUserData } from '@/services/api/user-api';
import { useUser } from '@auth0/nextjs-auth0/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type TFormLandingRental = {
  user: TUserData | undefined;
  service: Service;
};

export const useFormLandingCampus = ({ service, user }: TFormLandingRental) => {
  const Mixpanel = useMixPanel();
  const [isOpenSummaryModal, setIsOpenSummaryModal] = useState(false);
  const [isSaleCreated, setIsSaleCreated] = useState(false);
  const [isOpenRegisteredUserModal, setIsOpenRegisteredUserModal] =
    useState(false);
  const userEmailRef = useRef('');
  const emailHint = userEmailRef.current.replace(/\+/g, '%2B');

  const isDataExpired = useRootStore((store) => store.isDataExpired);
  const purchaseCompleted = useRootStore((store) => store.purchaseCompleted);
  const setPurchaseCompleted = useRootStore(
    (store) => store.setPurchaseCompleted
  );

  const { mutate, isPending: isPendingSale } = useCreateSale();
  const {
    data: isUserExists,
    isSuccess,
    refetch: checkUserEmail,
    dataUpdatedAt
  } = useQuery({
    queryKey: ['checkUser'],
    queryFn: () => api.user.userExistsByEmail(userEmailRef.current),
    enabled: false
  });

  const cleanStepServicesData = useRootStore(
    (store) => store.cleanStepServicesData
  );

  const schema = z.object({
    serviceDivisionGroups: z.array(z.string()).min(1),
    user: z.object({
      firstName: firstNameSchema,
      lastName: lastNameSchema,
      phone: phoneSchema,
      identification: z
        .object({
          number: z.string(),
          type: z.string()
        })
        .partial(),
      email: emailSchema,
      address: z.string().min(1),
      location: z.string().min(1),
      postalCode: z.string().min(1)
    }),
    parent: z
      .object({
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        phone: z.string().optional(),
        email: z.string().optional(),
        address: z.string().optional(),
        location: z.string().optional(),
        postalCode: z.string().optional(),
        identification: z
          .object({
            number: z.string().optional(),
            type: z.string().optional()
          })
          .optional()
      })
      .superRefine((data, ctx) => {
        const {
          firstName,
          lastName,
          phone,
          email,
          address,
          location,
          postalCode,
          identification
        } = data;

        const values = [
          firstName,
          lastName,
          phone,
          email,
          address,
          location,
          postalCode,
          identification?.type,
          identification?.number
        ];

        const isEmpty = values.every((val) => !val || val === '');

        if (isEmpty) return; // Nada que validar

        const entries = [
          { key: 'firstName', value: firstName },
          { key: 'lastName', value: lastName },
          { key: 'phone', value: phone },
          { key: 'email', value: email },
          { key: 'address', value: address },
          { key: 'location', value: location },
          { key: 'postalCode', value: postalCode },
          { key: 'identification.type', value: identification?.type },
          { key: 'identification.number', value: identification?.number }
        ];

        for (const entry of entries) {
          if (!entry.value || entry.value.length < 2) {
            if (entry.key === 'email') {
              ctx.addIssue({
                path: entry.key.split('.'),
                message: 'schemas:common.emailRequired',
                code: z.ZodIssueCode.custom
              });
            } else {
              ctx.addIssue({
                path: entry.key.split('.'),
                message: 'schemas:common.fieldRequired',
                code: z.ZodIssueCode.custom
              });
            }
          }
        }
      }),
    student: z.object({
      id: z.string().optional(),
      firstName: firstNameSchema,
      lastName: lastNameSchema,
      identification: z
        .object({
          number: z.string(),
          type: z.string()
        })
        .partial(),
      birthdate: birthdateSchema({
        minAge: service?.minAge,
        maxAge: service?.maxAge
      }),
      illness: z.discriminatedUnion(
        'value',
        [
          z.object({
            value: z.literal<boolean>(true, {
              invalid_type_error: 'schemas:common.fieldObligatory'
            }),
            answer: z
              .string({ required_error: 'schemas:common.fieldObligatory' })
              .min(1, 'schemas:common.fieldRequired')
          }),
          z.object({
            value: z.literal<boolean>(false, {
              invalid_type_error: 'schemas:common.fieldObligatory'
            }),
            answer: z
              .string()
              .optional()
              .transform(() => '')
          })
        ],
        {
          errorMap: (issue, ctx) => ({
            message:
              issue.code === 'invalid_union_discriminator'
                ? `schemas:common.fieldObligatory`
                : ctx.defaultError
          })
        }
      )
    }),
    acceptDataPrivacy: z.boolean().refine((val) => val === true, {
      message: 'schemas:common.dataPrivacyRequired'
    }),
    acceptTakePhotos: z.boolean().optional(),
    authOfferProducts: z.boolean().optional()
  });

  const userConsumerData = user?.consumers?.find(
    (consumer) => consumer.isOwnUser
  );

  const defaultValuesUser = {
    firstName: userConsumerData?.firstName ?? '',
    lastName: userConsumerData?.lastName ?? '',
    phone: userConsumerData?.phone?.toString() ?? '',
    identification: {
      number: userConsumerData?.identification?.number ?? '',
      type: userConsumerData?.identification?.type ?? ''
    },
    email: userConsumerData?.email ?? '',
    address: userConsumerData?.address ?? '',
    location: userConsumerData?.location ?? '',
    postalCode: userConsumerData?.postalCode ?? ''
  };

  const defaultValues = {
    serviceDivisionGroups: [],
    user: defaultValuesUser,
    parent: {
      firstName: '',
      lastName: '',
      phone: '',
      identification: {
        number: '',
        type: ''
      },
      email: '',
      address: '',
      location: '',
      postalCode: ''
    },
    student: {
      id: '',
      firstName: '',
      lastName: '',
      identification: {
        number: '',
        type: ''
      },
      birthdate: undefined,
      illness: {
        value: false,
        answer: ''
      }
    },
    acceptDataPrivacy: false,
    acceptTakePhotos: false,
    authOfferProducts: false
  };
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues
  });

  const onSubmit = () => {
    Mixpanel.track('Create new sale after complete booking', {
      screen: 'New Booking Landing',
      Activity: service?.name
    });
    if (service) {
      const {
        user,
        serviceDivisionGroups,
        parent,
        student,
        acceptDataPrivacy,
        acceptTakePhotos,
        authOfferProducts
      } = form.getValues();
      const { id: studentId, ...rest } = student;
      const studentParsed = {
        ...rest,
        birthdate: student.birthdate.toISOString(),
        group: null,
        parents: [user, parent],
        acceptDataPrivacy,
        acceptTakePhotos,
        authOfferProducts
      };
      const payload = {
        service: service.id,
        email: user.email,
        phone: user.phone,
        firstName: user.firstName,
        lastName: user.lastName,
        serviceDivisionGroups,
        consumers: studentId ? [studentId] : [studentParsed],
        paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440'
      };

      mutate(payload);
      setIsSaleCreated(true);
    }
  };

  const userEmailField = form.watch('user.email');

  const handleBlur = async () => {
    const isEmailValid = await form.trigger('user.email');
    if (userEmailField !== '' && isEmailValid) {
      userEmailRef.current = userEmailField;
      checkUserEmail();
    }
  };

  useEffect(() => {
    const isExpired = isDataExpired();
    if (isExpired) {
      cleanStepServicesData();
    }
    if (purchaseCompleted) {
      setPurchaseCompleted(false);
      cleanStepServicesData();
    }

    Mixpanel.track('Starting new booking', {
      screen: 'New Booking Landing',
      Activity: service?.name
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuccess && isUserExists && !user?.email) {
      setIsOpenRegisteredUserModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  return {
    form,
    user,
    service,
    isPendingSale,
    isOpenSummaryModal,
    isSaleCreated,
    mutate,
    onSubmit,
    setIsOpenSummaryModal,
    setIsSaleCreated,
    handleBlur,
    emailHint,
    isOpenRegisteredUserModal,
    setIsOpenRegisteredUserModal
  };
};
